<template>
  <el-dialog
    v-model="dialogVisible"
    :width="messages.length > 0 ? '1208px' : '628px'"
    :close-on-click-modal="false"
    :before-close="handleBeforeClose"
    top="40px"
    class="p-10 timesheet-entry-dialog"
  >
    <!-- Custom header with title and status badge -->
    <template #header>
      <div class="flex justify-between items-center w-full">
        <span class="text-[36px] font-semibold">{{ dialogTitle }}</span>
        <div v-if="mode === 'edit'" class="status-badge px-4 py-2 rounded-lg text-sm font-medium" :class="statusClass">
          {{ statusText }}
        </div>
      </div>
    </template>
    <div 
      class="grid" 
      :class="messages.length > 0 ? 'grid-cols-2' : 'grid-cols-1'"
      v-loading="isGlobalLoading"
      element-loading-text="Processing..."
    >
      <div 
        :class="messages.length > 0 ? 'border-r-2 pr-[20px] task-border-right min-h-[400px]' : ''"
      >
        <TimesheetEntryDetail 
          v-show="!(mode === 'edit' && isLoadingData)"
          ref="timesheetEntryDetailRef" 
          :is-edit-mode="mode === 'edit'" 
          :disabled="isFormDisabled" 
          :timesheet-data="editTimesheetData" 
        />
            <div class="text-right pt-[20px]">
      <el-button @click="close" class="btn-cancel">Close</el-button>
      
      <!-- Create mode buttons -->
      <template v-if="mode === 'create'">
        <el-button @click="save" class="btn-save">Save</el-button>
        <el-button @click="submit" class="btn-submit" :loading="isSubmitting">Submit</el-button>
      </template>

      <!-- Edit mode buttons - Draft status (owner only) -->
      <template v-else-if="mode === 'edit' && timesheetStatus === 0 && isOwner">
        <el-button @click="handleDelete" class="btn-delete btn-delete-white" v-if="!hasBeenSubmitted">Delete</el-button>
        <el-button @click="handleCancelTimesheet" class="btn-cancel-task" v-if="hasBeenSubmitted">Cancel</el-button>
        <el-button @click="save" class="btn-save">Save</el-button>
        <el-button @click="submit" class="btn-submit" :loading="isSubmitting">Submit</el-button>
      </template>

      <!-- Edit mode buttons - Submitted status (approver only) -->
      <template v-else-if="mode === 'edit' && timesheetStatus === 1 && !isOwner && !isUserAdmin">
        <el-button @click="handleDecline" class="btn-decline">Decline</el-button>
        <el-button @click="handleApprove" class="btn-approve" :loading="isApproving">Approve</el-button>
      </template>

      <!-- Edit mode buttons - Submitted status (owner - can recall) -->
      <template v-else-if="mode === 'edit' && timesheetStatus === 1 && isOwner">
        <el-button @click="handleRecall" class="btn-recall">Recall</el-button>
      </template>

      <!-- Edit mode buttons - Declined status (owner - can recall) -->
      <template v-else-if="mode === 'edit' && timesheetStatus === 3 && isOwner">
        <el-button @click="handleRecall" class="btn-recall">Recall</el-button>
      </template>



      <!-- Edit mode buttons - Approved status (view only, just close) -->
    </div>
      </div>
      <!-- Messages show immediately, no loading -->
      <MessageTimeSheet v-if="messages.length > 0" :messages="messages" />
    </div>
    <!-- Action buttons - different based on mode and status -->

  </el-dialog>

  <!-- Validation Warning Dialog -->
  <ConfirmDialog
    v-model:visible="showValidationDialog"
    :title="validationResult?.title || 'Submitting'"
    :message="validationResult?.message || ''"
    :lbl-btn-submit="validationResult?.hasConfirmOption ? 'Confirm' : ''"
    lbl-btn-cancel="Fix"
    icon-popup="btn-fix"
    cancel-type="danger"
    :width="400"
    @submit="handleValidationConfirm"
    @cancel="showValidationDialog = false"
  />

  <ConfirmDialog
    v-model:visible="showConfirmDialog"
    title="Submitting"
    :message="confirmMessage"
    lbl-btn-submit="Yes"
    lbl-btn-cancel="No"
    icon-popup="icon-popup-submit"
    icon-btn-name="check-mark-circle"
    :width="400"
    @submit="handleConfirmSubmit"
    @cancel="showConfirmDialog = false"
  />

  <ConfirmDialog
    v-model:visible="showApproveDialog"
    title="Approving"
    message="The timesheet will be approved. Are you sure?"
    lbl-btn-submit="Yes"
    lbl-btn-cancel="No"
    icon-popup="icon-popup-submit"
    icon-btn-name="check-mark-circle"
    :width="400"
    @submit="handleConfirmApprove"
    @cancel="showApproveDialog = false"
  />

  <!-- Decline Dialog with Reason Input -->
  <PopupConfirm
    type="Decline"
    message="Are you sure to decline the timesheet?"
    lbl-btn-submit="Yes"
    icon-btn-name="check-mark-circle"
    :loading="isDeclining"
    @submit="handleConfirmDecline"
    ref="popupConfirmDecline"
  />

  <ConfirmDialog
    v-model:visible="showDeleteDialog"
    title="Deleting"
    message="Your data will be deleted. Are you sure?"
    lbl-btn-submit="Delete"
    lbl-btn-cancel="Cancel"
    icon-popup="icon-popup-delete"
    icon-btn-name="delete"
    :width="400"
    @submit="handleConfirmDelete"
    @cancel="showDeleteDialog = false"
  />

  <!-- Cancel (Void) Confirmation Dialog -->
  <ConfirmDialog
    v-model:visible="showCancelDialog"
    title="Cancelling"
    :message="'Your timesheet will be cancelled.\nAre you sure?'"
    lbl-btn-submit="Yes"
    lbl-btn-cancel="No"
    icon-popup="canceling-red"
    icon-btn-name="check-mark-circle"
    :width="400"
    @submit="handleConfirmCancel"
    @cancel="showCancelDialog = false"
  />

  <!-- PO Selection Dialog for Contractors -->
  <POSelectionDialog
    v-model:visible="showPOSelectionDialog"
    :worker-id="contractorWorkerId"
    :company-id="contractorCompanyId"
    @confirm="handlePOConfirm"
    @cancel="handlePOCancel"
  />
</template>

<script lang="ts" setup>
import { computed, reactive, ref, inject, watch, nextTick } from "vue";
import { useStore } from "vuex";
import TimesheetEntryDetail from "@/components/v2/TimesheetEntryDetail.vue";
import MessageTimeSheet from "@/components/MessageTimeSheet.vue";
import useTimesheetEntryDialog from "@/composables/v2/useTimesheetEntryDialog";
import { useTimesheetValidation, ValidationResult } from "@/composables/v2/useTimesheetValidation";
import ConfirmDialog from "@/components/v2/ConfirmDialog.vue";
import POSelectionDialog from "@/components/v2/POSelectionDialog.vue";
import PopupConfirm from "@/components/PopupConfirm.vue";
import { MESSAGE_CODES, USER_TYPE_USER_ADMINISTRATOR } from "@/core/constants";
import { diffMinutes, getCurrentTimeZoneId } from "@/utils/time";
import moment from "moment";


const store = useStore();
const notify = inject("notify", (type: string, message: string) => ({}));
const currentUser = computed(() => store.getters["auth/currentUser"]);

const { validateAll } = useTimesheetValidation();

const { 
  dialogVisible, 
  mode,
  editTimesheetId,
  editTimesheetData,
  isFromNotification,
  handleBeforeClose: originalHandleBeforeClose, 
  closeEdit 
} = useTimesheetEntryDialog();

const timesheetEntryDetailRef = ref();
const showConfirmDialog = ref(false);
const showApproveDialog = ref(false);
const showDeleteDialog = ref(false);
const showCancelDialog = ref(false);
const showValidationDialog = ref(false);
const validationResult = ref<ValidationResult | null>(null);
const pendingSubmitAfterConfirm = ref(false);
const isSubmitting = ref(false);
const isApproving = ref(false);
const isDeclining = ref(false);
const isRecalling = ref(false);
const isCancelling = ref(false);
const isDeleting = ref(false);
const confirmMessage = "Your timesheet will be submitted.\nAre you sure?";
const popupConfirmDecline = ref<InstanceType<typeof PopupConfirm>>();
const messages = ref<MessageEventModel[]>([]);

// Contractor PO selection
const showPOSelectionDialog = ref(false);


// Get worker info from timesheetLines (fallback chain)
const contractorWorkerId = computed(() => {
  // Try from User.Resource first (if full data available)
  const fromUser = editTimesheetData.value?.User?.WorkerId || 
                   editTimesheetData.value?.user?.WorkerId;
  if (fromUser) return fromUser;
  
  // Fallback to timesheetLines[0].OwnerWorkerId
  const lines = editTimesheetData.value?.timesheetLines || editTimesheetData.value?.TimesheetLines;
  return lines?.[0]?.OwnerWorkerId || lines?.[0]?.ownerWorkerId || '';
});

// Get companyId from editTimesheetData.User
const contractorCompanyId = computed(() => {
  // Get from User (dataTimesheet.User)
  const user = editTimesheetData.value?.User as any || 
               editTimesheetData.value?.user as any;
  const fromUser = user?.CompanyID || user?.companyId || user?.CompanyId;
  if (fromUser) return fromUser;
  
  // Fallback to timesheetLines[0].CompanyID
  const lines = editTimesheetData.value?.timesheetLines || editTimesheetData.value?.TimesheetLines;
  return lines?.[0]?.CompanyID || lines?.[0]?.companyId || '';
});

const isContractor = computed(() => {
  // WorkerType: 0 = Employee, 1 = Contractor
  // Try from User.Resource first (if full data available)
  const fromUser = editTimesheetData.value?.User?.Resource?.WorkerType ?? 
                   editTimesheetData.value?.user?.Resource?.WorkerType;
  if (fromUser !== undefined && fromUser !== null) {
    return Number(fromUser) === 1;
  }
  
  // Fallback to timesheetLines[0].WorkerType
  const lines = editTimesheetData.value?.timesheetLines || editTimesheetData.value?.TimesheetLines;
  const workerType = lines?.[0]?.WorkerType ?? lines?.[0]?.workerType;
  return Number(workerType) === 1;
});

const selectedPOLine = ref<any>(null);

// Computed properties for edit mode
const dialogTitle = computed(() => 
  mode.value === 'edit' ? `Timesheet #${editTimesheetId.value}` : 'New Timesheet'
);

const timesheetStatus = computed(() => 
  editTimesheetData.value?.timesheetStatus ?? 0
);

const isOwner = computed(() => 
  editTimesheetData.value?.userId === currentUser.value?.userId
);

const isUserAdmin = computed(() => 
  currentUser.value?.userType === USER_TYPE_USER_ADMINISTRATOR
);

const isFormDisabled = computed(() => {
  // Disable form if in edit mode and:
  // 1. Draft: only owner can edit
  // 2. Submitted: owner is read-only (waiting for approval)
  // 3. Submitted: UserAdmin is read-only (cannot approve/decline)
  // 4. Approved: always read-only
  if (mode.value !== 'edit') return false;
  
  const status = timesheetStatus.value;
  
  // Approved, Declined, and Cancelled timesheets are always read-only
  if (status === 2 || status === 3 || status === 5) return true;
  
  // Submitted timesheets are read-only for owner
  if (status === 1 && isOwner.value) return true;
  
  // Submitted timesheets are read-only for UserAdmin (cannot approve/decline)
  if (status === 1 && isUserAdmin.value) return true;
  
  // Draft timesheets are read-only for non-owners
  if (status === 0 && !isOwner.value) return true;
  
  return false;
});

const hasBeenSubmitted = computed(() => 
  (editTimesheetData.value?.submittedCount ?? 0) >= 1
);

const statusText = computed(() => {
  const status = timesheetStatus.value;
  switch (status) {
    case 0: return 'Draft';
    case 1: return 'Pending Approval';
    case 2: return 'Approved';
    case 3: return 'Declined';
    case 5: return 'Cancelled';
    default: return 'Unknown';
  }
});

const statusClass = computed(() => {
  const status = timesheetStatus.value;
  switch (status) {
    case 0: return 'bg-gray-100 text-gray-700';
    case 1: return 'bg-yellow-100 text-yellow-700';
    case 2: return 'bg-green-100 text-green-700';
    case 3: return 'status-declined';
    case 5: return 'bg-red-100 text-red-700';
    default: return 'bg-gray-100 text-gray-700';
  }
});

const isLoadingData = ref(false);
const isGlobalLoading = computed(() => 
  (mode.value === 'edit' && isLoadingData.value) || 
  isSubmitting.value || 
  isApproving.value || 
  isDeclining.value ||
  isRecalling.value ||
  isCancelling.value ||
  isDeleting.value
);
const loadedTimesheetId = ref<number | null>(null); // Guard to prevent duplicate loading

const loadTimesheet = async (id: number) => {
    isLoadingData.value = true
    loadedTimesheetId.value = id; // Mark as loading
    
    try {
      // Load messages FIRST (fast) to determine dialog width before showing loading state
      // Only load messages if opened from Notification
      if (isFromNotification.value) {
        const messagesResult = await store.dispatch('dashboard/actFetchMessage', {
          TimesheetHeaderId: id
        });
        messages.value = messagesResult || [];
      } else {
        messages.value = [];
      }
      
      // Now show loading state (width is already determined)
      
      // Load settings in parallel for approval flow (ContractorPOEnforcementMode check)
      store.dispatch('setting/getSettings');
      
      const fullData = await store.dispatch('v2/getTimesheetDetail', id);
      
      if (fullData) {
        await nextTick();
        // Load the full data into the form
        timesheetEntryDetailRef.value?.loadTimesheetData({
          ...fullData,
          timesheetHeaderId: fullData.TimesheetHeaderId,
          timesheetStatus: fullData.TimesheetStatus,
          timesheetLines: fullData.TimesheetLines,
          startDate: fullData.StartDate,
          shift: fullData.Shift,
          userId: fullData.UserId,
          user: fullData.User,
          submittedCount: fullData.SubmittedCount || 0
        });

        // Update composable state for Dialog UI (Status Badge, Buttons)
        editTimesheetData.value = {
          timesheetHeaderId: fullData.TimesheetHeaderId,
          startDate: fullData.StartDate,
          shift: fullData.Shift,
          timesheetStatus: fullData.TimesheetStatus,
          userId: fullData.UserId,
          submittedCount: fullData.SubmittedCount || 0,
          timesheetLines: fullData.TimesheetLines,
          TimesheetLines: fullData.TimesheetLines, // for components accessing PascalCase
          User: fullData.User, 
          user: fullData.User
        };
      }
    } catch (error) {
      loadedTimesheetId.value = null; // Reset guard on error
    } finally {
      isLoadingData.value = false;
    }
};

watch([dialogVisible, editTimesheetId], async ([visible, id]) => {
  if (!visible) {
    loadedTimesheetId.value = null;
    messages.value = [];
    return;
  }
  
  // Edit mode: load timesheet data
  if (visible && id && mode.value === 'edit') {
    // Prevent duplicate loading
    if (loadedTimesheetId.value === id) {
      return;
    }
    await loadTimesheet(id);
    return;
  }
  
  if (visible && mode.value === 'create') {
    await nextTick();
    timesheetEntryDetailRef.value?.resetForm();
  }
}, { immediate: true });

// Override handleBeforeClose to reset form
const handleBeforeClose = (done?: () => void) => {
  timesheetEntryDetailRef.value?.resetForm();
  originalHandleBeforeClose(done);
};

// Override close to reset form
const close = () => {
  timesheetEntryDetailRef.value?.resetForm();
  closeEdit();
};

const save = async () => {
  // For draft (Save), we don't require validation - allow saving incomplete data
  
  // Get form data for Draft (status 0)
  const formData = await getFormData(false);

  try {
    isSubmitting.value = true;
    let result;
    if (editTimesheetId.value) {
      result = await store.dispatch("v2/updateTimesheet", { 
        id: editTimesheetId.value, 
        data: formData 
      });
    } else {
      result = await store.dispatch("v2/createTimesheet", formData);
    }
    notify("success", MESSAGE_CODES.MSG_SAVE_SUCCESS);
    timesheetEntryDetailRef.value?.resetForm();
    close();
    // Refresh dashboard list respecting current filters
    store.commit("dashboard/SET_LIST_TIME_SHEET", []);
    store.commit('timeSheetsApprove/SET_LOADING_TIME_SHEETS', true);
    await store.dispatch("dashboard/actLoadTimeSheetsWithApi", { page: 1 });
    await store.dispatch("dashboard/actCountTimeSheetStatus");
    store.commit('timeSheetsApprove/SET_LOADING_TIME_SHEETS', false);
    
  } catch (error: any) {
    const errorMessage = error?.response?.data?.message || error?.data?.message || error?.data?.value || MESSAGE_CODES.ERR_SERVER;
    notify("error", errorMessage);
  } finally {
    isSubmitting.value = false;
  }
};

const submit = async () => {
  const workingTimeValidation = timesheetEntryDetailRef.value?.validateWorkingTime();
  if (!workingTimeValidation?.isValid) {
    return;
  }

  const isValid = await timesheetEntryDetailRef.value?.validateAllForms();
  if (!isValid) {
    return;
  }

  // Run all validations
  const exposed = timesheetEntryDetailRef.value;
  if (exposed?.state) {
    const result = await validateAll({
      lines: exposed.state.lines,
      startDate: exposed.state.startDate,
      isNightShift: exposed.state.shift,
      excludeTimesheetId: editTimesheetId.value ?? undefined
    });

    if (result) {
      validationResult.value = result;
      pendingSubmitAfterConfirm.value = result.hasConfirmOption;
      showValidationDialog.value = true;
      return;
    }
  }

  showConfirmDialog.value = true;
};

const handleValidationConfirm = () => {
  showValidationDialog.value = false;
  if (pendingSubmitAfterConfirm.value) {
    showConfirmDialog.value = true;
  }
  pendingSubmitAfterConfirm.value = false;
};

const handleConfirmSubmit = async () => {
  try {
    isSubmitting.value = true;
    showConfirmDialog.value = false;
    
    const formData = await getFormData(true);
    
    if (!formData) {
      notify("error", "Failed to prepare form data");
      return;
    }

    let result;
    if (editTimesheetId.value) {
      result = await store.dispatch("v2/updateTimesheet", { 
        id: editTimesheetId.value, 
        data: formData 
      });
    } else {
      result = await store.dispatch("v2/createTimesheet", formData);
    }
    
    notify("success", MESSAGE_CODES.MSG_SAVE_SUCCESS);
    timesheetEntryDetailRef.value?.resetForm();
    close();
    // Refresh dashboard list respecting current filters
    store.commit("dashboard/SET_LIST_TIME_SHEET", []);
    store.commit('timeSheetsApprove/SET_LOADING_TIME_SHEETS', true);
    await store.dispatch("dashboard/actLoadTimeSheetsWithApi", { page: 1 });
    await store.dispatch("dashboard/actCountTimeSheetStatus");
    store.commit('timeSheetsApprove/SET_LOADING_TIME_SHEETS', false);


  } catch (error: any) {
    const errorMessage = error?.response?.data?.message || error?.data?.message || error?.data?.value || MESSAGE_CODES.ERR_SERVER;
    notify("error", errorMessage);
  } finally {
    isSubmitting.value = false;
  }
};

// Approval actions
const handleApprove = async () => {
  const settings = store.getters["setting/settings"];
  const contractorPORequired = settings?.ContractorPOEnforcementMode ?? false;
  
  if (contractorPORequired && isContractor.value) {
    showPOSelectionDialog.value = true;
  } else {
    showApproveDialog.value = true;
  }
};

// Handle PO selection confirm
const handlePOConfirm = async (poLine: any) => {
  selectedPOLine.value = poLine;
  showPOSelectionDialog.value = false;
  await handleConfirmApprove();
};

const handlePOCancel = () => {
  showPOSelectionDialog.value = false;
  selectedPOLine.value = null;
};

const handleConfirmApprove = async () => {
  try {
    isApproving.value = true;
    showApproveDialog.value = false;
    
    // Get exposed properties from TimesheetEntryDetail
    const exposed = timesheetEntryDetailRef.value;
    
    if (!exposed?.state) {
      notify("error", "Failed to get form data");
      return;
    }

    const { state, rentalTaskRefs, removedLineIds } = exposed;

    // rentalTaskRefs might be a ref or an array
    const taskRefsArray = Array.isArray(rentalTaskRefs) 
      ? rentalTaskRefs 
      : (rentalTaskRefs?.value ?? []);

    // Build lines payload - use async getTaskDataWithSignatures for signature upload
    const timesheetLines = await Promise.all(state.lines.map(async (line: any) => {
      const taskRef = taskRefsArray.find((ref: any) => 
        ref && (ref.taskId === line.id || ref?.$?.props?.taskId === line.id)
      );
      
      const taskDetail = taskRef?.getTaskDataWithSignatures 
        ? await taskRef.getTaskDataWithSignatures() 
        : (taskRef?.getTaskData?.() || {});
      
      const startTimeMinutes = line.startTime ? 
        line.startTime.getHours() * 60 + line.startTime.getMinutes() : null;
      let endTimeMinutes = line.endTime ? 
        line.endTime.getHours() * 60 + line.endTime.getMinutes() : null;
      // Handle night shift (crossing midnight): if endTime is before startTime, add 24 hours
      if (line.startTime && line.endTime && endTimeMinutes < startTimeMinutes) {
        endTimeMinutes += 1440; // 24 * 60 = 1440 minutes
      }
      const durationMinutes = line.startTime && line.endTime ? 
        diffMinutes(line.startTime, line.endTime) : 0;

      const timeZoneId = getCurrentTimeZoneId();
      const workDate = moment(state.startDate).startOf('day');
      const localStartTime = startTimeMinutes !== null ? workDate.clone().add(startTimeMinutes, 'minutes').format() : null;
      let localEndTimeMoment = endTimeMinutes !== null ? workDate.clone().add(endTimeMinutes, 'minutes') : null;
      if (localEndTimeMoment && endTimeMinutes < startTimeMinutes) {
        localEndTimeMoment.add(1, 'day');
      }
      const localEndTimeStr = localEndTimeMoment ? localEndTimeMoment.format() : null;

      return {
        timesheetLineId: line.originalLineId || null,
        timesheetLineMinutes: durationMinutes,
        timesheetLineType: line.type === 'Rental' ? 0 : 1,
        startTime: startTimeMinutes,
        endTime: endTimeMinutes,
        comments: taskDetail.comments || '',
        overHead: taskDetail.overHead || false,
        companyId: taskDetail.companyId || null,
        jobProjectId: taskDetail.jobProjectId || null,
        activityId: taskDetail.activityId || null,
        npCode: taskDetail.npCode || null,
        fieldService: taskDetail.fieldService || false,
        assetProjectId: taskDetail.assetProjectId || null,
        siteProjectId: taskDetail.siteProjectId || null,
        workOrderProjectId: taskDetail.workOrderProjectId || null,
        // Include timesheetAdditional with signature paths
        timesheetAdditional: taskDetail.timesheetAdditional || null,
        // Localized time fields
        timeZoneId: timeZoneId,
        localStartTime: localStartTime,
        localEndTime: localEndTimeStr,
      };

    }));

    // Build payload for approveWithChanges
    // removedLineIds might be a ref or an array depending on Vue's unwrapping
    const linesToRemoveRaw = Array.isArray(removedLineIds) 
      ? removedLineIds 
      : (removedLineIds?.value ?? []);
    
    // Convert Proxy to plain array for API
    const linesToRemoveArray = [...linesToRemoveRaw];
    
    const payload = {
      shift: state.shift,
      timesheetLines: [...timesheetLines], // Convert to plain array
      linesToRemove: linesToRemoveArray,
      poNumber: selectedPOLine.value?.poNumber || null,
      poLineId: selectedPOLine.value?.poLineId || null,
      poLotId: selectedPOLine.value?.poLotID || null,
      timezoneOffset: new Date().getTimezoneOffset(),
    };

    await store.dispatch("v2/approveTimesheetWithChanges", {
      id: editTimesheetId.value,
      payload
    });
    
    notify("success", "Timesheet approved successfully");
    close();
    // Refresh the list
    store.dispatch("timeSheetsApprove/actLoadTimeSheetWithParams", {});
  } catch (error: any) {
    const errorMessage = error?.data?.message || error?.message || MESSAGE_CODES.ERR_SERVER;
    notify("error", errorMessage);
  } finally {
    isApproving.value = false;
  }
};

const handleDecline = () => {
  if (popupConfirmDecline.value) {
    popupConfirmDecline.value.showDialog = true;
  }
};

const handleConfirmDecline = async (payload: { reason: string }) => {
  try {
    isDeclining.value = true;
    if (popupConfirmDecline.value) {
      popupConfirmDecline.value.showDialog = false;
    }
    
    await store.dispatch("v2/declineTimesheet", { 
      id: editTimesheetId.value,
      reason: payload.reason.trim()
    });
    
    notify("success", "Timesheet declined");
    close();
    store.dispatch("timeSheetsApprove/actLoadTimeSheetWithParams", {});
  } catch (error: any) {
    notify("error", error?.message || MESSAGE_CODES.ERR_SERVER);
  } finally {
    isDeclining.value = false;
  }
};

const handleDelete = () => {
  showDeleteDialog.value = true;
};

const handleConfirmDelete = async () => {
  try {
    showDeleteDialog.value = false;
    isDeleting.value = true;
    
    const deletePayload = {
      timesheetStatus: 6
    };
    
    await store.dispatch("v2/updateTimesheet", { 
      id: editTimesheetId.value, 
      data: deletePayload 
    });
    
    notify("success", MESSAGE_CODES.MSG_DELETE_SUCCESS);
    close();
    
    // Refresh dashboard lists
    store.commit("dashboard/SET_LIST_TIME_SHEET", []);
    store.commit('timeSheetsApprove/SET_LOADING_TIME_SHEETS', true);
    await store.dispatch("dashboard/actLoadTimeSheetsWithApi", { page: 1 });
    await store.dispatch("dashboard/actCountTimeSheetStatus");
    store.commit('timeSheetsApprove/SET_LOADING_TIME_SHEETS', false);
  } catch (error: any) {
    notify("error", error?.message || MESSAGE_CODES.ERR_SERVER);
  } finally {
    isDeleting.value = false;
  }
};

const handleRecall = async () => {
  try {
    isRecalling.value = true;
    await store.dispatch("v2/recallTimesheet", editTimesheetId.value);
    
    notify("success", "Timesheet recalled");
    
    // Reload timesheet data to reflect new status (Draft)
    loadedTimesheetId.value = null; 
    if (editTimesheetId.value !== null) {
      await loadTimesheet(editTimesheetId.value);
    }
    
    // Refresh dashboard lists
    store.commit("dashboard/SET_LIST_TIME_SHEET", []);
    store.dispatch("timeSheetsApprove/actLoadTimeSheetWithParams", {});
  } catch (error: any) {
    notify("error", error?.message || MESSAGE_CODES.ERR_SERVER);
  } finally {
    isRecalling.value = false;
  }
};

// Cancel (Void) timesheet - sets status to 5
const handleCancelTimesheet = () => {
  showCancelDialog.value = true;
};

const handleConfirmCancel = async () => {
  try {
    showCancelDialog.value = false;
    isCancelling.value = true;
    
    await store.dispatch("v2/cancelTimesheet", editTimesheetId.value);
    
    notify("success", "Timesheet cancelled");
    close();
    
    // Refresh dashboard lists
    store.commit("dashboard/SET_LIST_TIME_SHEET", []);
    store.dispatch("timeSheetsApprove/actLoadTimeSheetWithParams", {});
  } catch (error: any) {
    notify("error", error?.message || MESSAGE_CODES.ERR_SERVER);
  } finally {
    isCancelling.value = false;
  }
};

const getFormData = async (isSubmit: boolean = false) => {
  const { state, rentalTaskRefs } = timesheetEntryDetailRef.value || {};
  if (!state) {
    return null;
  }

  // Use Promise.all for async signature upload
  const timesheetLines = await Promise.all(state.lines.map(async (line: any) => {
    const taskRef = rentalTaskRefs?.find((ref: any) => 
      ref && (ref.taskId === line.id || ref?.$?.props?.taskId === line.id)
    );
    
    // Use async getTaskDataWithSignatures (uploads signatures) or fallback to sync getTaskData
    const taskDetail = taskRef?.getTaskDataWithSignatures 
      ? await taskRef.getTaskDataWithSignatures() 
      : (taskRef?.getTaskData?.() || {});
    
    const startTimeMinutes = line.startTime ? 
      line.startTime.getHours() * 60 + line.startTime.getMinutes() : null;
    let endTimeMinutes = line.endTime ? 
      line.endTime.getHours() * 60 + line.endTime.getMinutes() : null;
    
    // Handle night shift (crossing midnight): if endTime is before startTime, add 24 hours
    if (line.startTime && line.endTime && endTimeMinutes < startTimeMinutes) {
      endTimeMinutes += 1440; // 24 * 60 = 1440 minutes
    }
    const durationMinutes = line.startTime && line.endTime ? 
      diffMinutes(line.startTime, line.endTime) : 0;

    const timeZoneId = getCurrentTimeZoneId();
    const workDate = moment(state.startDate).startOf('day');
    const localStartTime = startTimeMinutes !== null ? workDate.clone().add(startTimeMinutes, 'minutes').format() : null;
    let localEndTimeMoment = endTimeMinutes !== null ? workDate.clone().add(endTimeMinutes, 'minutes') : null;
    if (localEndTimeMoment && endTimeMinutes < startTimeMinutes) {
      localEndTimeMoment.add(1, 'day');
    }
    const localEndTimeStr = localEndTimeMoment ? localEndTimeMoment.format() : null;

    return {
      timesheetLineMinutes: durationMinutes,
      timesheetLineType: line.type === 'Rental' ? 0 : 1,
      comments: taskDetail.comments || '',
      startTime: startTimeMinutes,
      endTime: endTimeMinutes,
      overHead: taskDetail.overHead || false,
      companyId: taskDetail.companyId || null,
      jobProjectId: taskDetail.jobProjectId || null,
      activityId: taskDetail.activityId || null,
      npCode: taskDetail.npCode || null,
      fieldService: taskDetail.fieldService || false,
      assetProjectId: taskDetail.assetProjectId || null,
      siteProjectId: taskDetail.siteProjectId || null,
      workOrderProjectId: taskDetail.workOrderProjectId || null,
      timesheetAdditional: taskDetail.timesheetAdditional || null,
      // Localized time fields
      timeZoneId: timeZoneId,
      localStartTime: localStartTime,
      localEndTime: localEndTimeStr,
    };

  }));

  const startTimeString = state.startDate ? 
    new Date(state.startDate).toISOString().split('T')[1].split('.')[0] : '08:00:00';

  return {
    startDate: state.startDate ? new Date(state.startDate).toISOString() : new Date().toISOString(),
    startTime: startTimeString,
    shift: state.shift,
    shiftType: state.shift ? 'Night' : 'Day',
    timesheetStatus: isSubmit ? 1 : 0,
    timesheetLines: timesheetLines,
    timezoneOffset: new Date().getTimezoneOffset(),
  };
};


</script>

<style lang="scss">
.timesheet-entry-dialog {
  .el-dialog__header {
    border-bottom: none !important;
  }
  .el-dialog__title {
    color: #231F20;
  }
  border: none !important;
  box-shadow: 0px 8px 24px 0px rgba(31, 35, 53, 0.48) !important;
  .btn-submit, .btn-approve {
    background-color: #246bfd !important;
    border-color: #246bfd !important;
    color: #ffffff !important;
    
    &:hover {
      opacity: 0.9;
    }
  }

  .btn-save {
    background-color: #0cac59 !important;
    border-color: #0cac59 !important;
    color: #ffffff !important;
    
    &:hover {
      opacity: 0.9;
    }
  }

  .btn-decline {
    background-color: #c84c0f !important;
    border-color: #c84c0f !important;
    color: #ffffff !important;

    &:hover {
      opacity: 0.9;
    }
  }

  .btn-delete {
    border-color: #ed1c2d !important;
    color: #ffffff !important;

    &:hover {
      opacity: 0.9;
    }
  }

  .btn-recall {
    background-color: #f6c22d !important;
    border-color: #f6c22d !important;
    color: #ffffff !important;

    &:hover {
      opacity: 0.9;
    }
  }

  .btn-cancel-task {
    border: 1px solid #ed1c2d !important;
    background-color: #ffffff !important;
    color: #ed1c2d !important;
  }

  .btn-save {
    width: 97px;
  }

  .btn-submit {
    width: 112px;
  }

  .form-group { margin-bottom: 12px; }
  .form-label { display: block; margin-bottom: 6px; font-weight: 600; }

  .status-declined {
    background-color: rgba(200, 76, 15, 0.1);
    color: #C84C0F;
  }
}


</style>

